import { Payout } from '../billingData';
import { FullProperty, PaymentEvent } from '../../../models/fullProperty';

/**
 * Fetches PaymentEvent documents from Firestore and transforms them
 * into Payout objects for the billing dashboard.
 */
export function fetchPayouts(restaurant: FullProperty): Payout[] {
  // Sort by date ascending
  restaurant.paymentHistory.sort((a, b) => new Date(a.paymentDate).getTime() - new Date(b.paymentDate).getTime());

  // Group by month (YYYY-MM)
  const grouped: Record<string, number> = {};
  restaurant.paymentHistory.forEach((evt) => {
    const date = new Date(evt.paymentDate);
    const monthKey = date.toISOString().slice(0, 7); // e.g. "2025-05"
    grouped[monthKey] = (grouped[monthKey] || 0) + evt.amount;
  });

  // Build Payout array
  const payouts: Payout[] = Object.entries(grouped).map(([month, totalCents]) => {
    const [year, mon] = month.split('-');
    const start = new Date(Number(year), Number(mon) - 1, 1);
    const end = new Date(Number(year), Number(mon), 0);
    return {
      id: `P-${month}`,
      date: end.toISOString().slice(0, 10),
      periodStart: start.toISOString().slice(0, 10),
      periodEnd: end.toISOString().slice(0, 10),
      amount: Math.round(totalCents) / 100, // convert cents to rands
      status: 'Paid',
    };
  });

  // Optionally, mark the latest month as pending if within current month
  const currentMonth = new Date().toISOString().slice(0, 7);
  return payouts.map((p) => {
    if (p.id === `P-${currentMonth}`) {
      return { ...p, status: 'Paid' };
    }
    return p;
  });
}

export const downloadPayoutsCSV = (payouts: Payout[]) => {
  const headers = ['ID', 'Date', 'Period Start', 'Period End', 'Amount', 'Status'];

  const rows = payouts.map(p => [
    p.id,
    p.date,
    p.periodStart,
    p.periodEnd,
    p.amount, // already formatted as a currency
    p.status
  ]);

  const csvContent = [headers, ...rows]
    .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `payouts-${new Date().toISOString().slice(0, 10)}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url); // Clean up
};